import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useTranslation } from "react-i18next";
import { LayoutGrid, LogOut, User, Gift, Shield, Settings, ArrowLeft, ChevronRight, Sun, Moon, Monitor, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/hooks/useTheme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Check } from "lucide-react";

interface UserMenuProps {
  variant?: "default" | "dashboard";
}

const UserMenu = (_: UserMenuProps) => {
  const { user, profile, signOut } = useAuth();
  const { isAdmin } = useAdminCheck();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [themeSheetOpen, setThemeSheetOpen] = useState(false);
  const [langSheetOpen, setLangSheetOpen] = useState(false);

  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, mobileOpen]);

  if (!user) return null;

  const displayName =
    profile?.display_name || user.user_metadata?.display_name || user.email || "User";
  const avatarUrl =
    profile?.avatar_url ||
    `https://api.dicebear.com/9.x/dylan/svg?seed=${encodeURIComponent(user.email || user.id)}`;

  const go = (path: string) => {
    setMobileOpen(false);
    navigate(path);
  };

  const isArabic = i18n.language === "ar";
  const changeLanguage = (lang: "en" | "ar") => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    setLangSheetOpen(false);
  };
  const chooseTheme = (t: "light" | "dark" | "system") => {
    setTheme(t);
    setThemeSheetOpen(false);
  };
  const themeLabel = theme === "light"
    ? t("theme.light", { defaultValue: "Light" })
    : theme === "dark"
      ? t("theme.dark", { defaultValue: "Dark" })
      : t("theme.system", { defaultValue: "System" });
  const ThemeIcon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;

  const items: Array<{ icon: typeof User; label: string; path: string; accent?: boolean }> = [
    { icon: User, label: t("nav.profile", { defaultValue: "Profile" }), path: "/profile" },
    { icon: LayoutGrid, label: t("nav.myCards", { defaultValue: "My Cards" }), path: "/dashboard" },
    { icon: Gift, label: t("nav.wishlists", { defaultValue: "Wish Lists" }), path: "/wishlists" },
  ];
  if (isAdmin) {
    items.push({ icon: Shield, label: t("nav.adminDashboard", { defaultValue: "Admin Dashboard" }), path: "/admin", accent: true });
  }

  const AvatarButton = (
    <button
      type="button"
      onClick={isMobile ? () => setMobileOpen(true) : undefined}
      className="w-10 h-10 rounded-full overflow-hidden bg-muted flex items-center justify-center ring-2 ring-transparent hover:ring-primary/30 focus:outline-none focus-visible:ring-primary transition-all"
      aria-label={displayName}
    >
      <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
    </button>
  );

  if (isMobile) {
    return (
      <>
        {AvatarButton}
        {createPortal(
          <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              className="fixed inset-0 z-[100] bg-background flex flex-col"
            >
              {/* Header with back */}
              <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
                <div className="flex items-center gap-2 px-4 py-3">
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors text-foreground"
                    aria-label="Back"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h1 className="text-base font-semibold text-foreground">
                    {t("nav.profile", { defaultValue: "Profile" })}
                  </h1>
                </div>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
                {/* User identity */}
                <div className="flex items-center gap-4 px-2 pt-6 pb-5">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-muted ring-2 ring-background shadow-md flex-shrink-0">
                    <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0 text-left rtl:text-right">
                    <p className="text-base font-semibold text-foreground truncate">{displayName}</p>
                    {user.email && (
                      <p className="text-sm text-muted-foreground truncate mt-0.5">{user.email}</p>
                    )}
                  </div>
                </div>

                {/* Menu items */}
                <div className="px-2 space-y-1">
                  {items.map(({ icon: Icon, label, path, accent }) => (
                    <button
                      key={path}
                      onClick={() => go(path)}
                      className={`w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-muted active:bg-muted/70 transition-colors text-left ${
                        accent ? "text-primary" : "text-foreground"
                      }`}
                    >
                      <Icon size={20} className={accent ? "text-primary" : "text-muted-foreground"} />
                      <span className="flex-1 text-[15px] font-medium">{label}</span>
                      <ChevronRight size={18} className="text-muted-foreground/60 rtl:rotate-180" />
                    </button>
                  ))}
                </div>

                {/* Preferences: theme + language */}
                <div className="px-2 mt-4">
                  <div className="border-t border-border pt-2 space-y-1">
                    <button
                      onClick={() => setThemeSheetOpen(true)}
                      className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-muted active:bg-muted/70 transition-colors text-left text-foreground"
                    >
                      <ThemeIcon size={20} className="text-muted-foreground" />
                      <span className="flex-1 text-[15px] font-medium">
                        {t("nav.theme", { defaultValue: "Theme" })}
                      </span>
                      <span className="text-[13px] text-muted-foreground">{themeLabel}</span>
                      <ChevronRight size={18} className="text-muted-foreground/60 rtl:rotate-180" />
                    </button>
                    <button
                      onClick={() => setLangSheetOpen(true)}
                      className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-muted active:bg-muted/70 transition-colors text-left text-foreground"
                    >
                      <Globe size={20} className="text-muted-foreground" />
                      <span className="flex-1 text-[15px] font-medium">
                        {t("nav.language", { defaultValue: "Language" })}
                      </span>
                      <span className="text-[13px] text-muted-foreground">{isArabic ? "العربية" : "English"}</span>
                      <ChevronRight size={18} className="text-muted-foreground/60 rtl:rotate-180" />
                    </button>
                  </div>
                </div>

                <div className="px-2 mt-4">
                  <div className="border-t border-border pt-2">
                    <button
                      onClick={() => {
                        setMobileOpen(false);
                        signOut();
                      }}
                      className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-muted active:bg-muted/70 transition-colors text-left text-muted-foreground"
                    >
                      <LogOut size={20} />
                      <span className="flex-1 text-[15px] font-medium">
                        {t("nav.logout", { defaultValue: "Logout" })}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
          document.body,
        )}

        {/* Theme bottom sheet */}
        <Sheet open={themeSheetOpen} onOpenChange={setThemeSheetOpen}>
          <SheetContent side="bottom" className="z-[110] rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
            <SheetHeader>
              <SheetTitle className="text-left rtl:text-right">
                {t("nav.theme", { defaultValue: "Theme" })}
              </SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-1">
              {([
                { value: "light", label: t("theme.light", { defaultValue: "Light" }), Icon: Sun },
                { value: "dark", label: t("theme.dark", { defaultValue: "Dark" }), Icon: Moon },
                { value: "system", label: t("theme.system", { defaultValue: "System" }), Icon: Monitor },
              ] as const).map(({ value, label, Icon }) => (
                <button
                  key={value}
                  onClick={() => chooseTheme(value)}
                  className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-muted active:bg-muted/70 transition-colors text-left text-foreground"
                >
                  <Icon size={20} className="text-muted-foreground" />
                  <span className="flex-1 text-[15px] font-medium">{label}</span>
                  {theme === value && <Check size={18} className="text-primary" />}
                </button>
              ))}
            </div>
          </SheetContent>
        </Sheet>

        {/* Language bottom sheet */}
        <Sheet open={langSheetOpen} onOpenChange={setLangSheetOpen}>
          <SheetContent side="bottom" className="z-[110] rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
            <SheetHeader>
              <SheetTitle className="text-left rtl:text-right">
                {t("nav.language", { defaultValue: "Language" })}
              </SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-1">
              {([
                { value: "en", label: "English" },
                { value: "ar", label: "العربية" },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => changeLanguage(value)}
                  className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-muted active:bg-muted/70 transition-colors text-left text-foreground"
                >
                  <Globe size={20} className="text-muted-foreground" />
                  <span className="flex-1 text-[15px] font-medium">{label}</span>
                  {i18n.language === value && <Check size={18} className="text-primary" />}
                </button>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{AvatarButton}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 py-2">
          <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
          {user.email && (
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          )}
        </div>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={() => navigate("/profile")} className="gap-2 cursor-pointer">
          <User size={14} />
          {t("nav.profile", { defaultValue: "Profile" })}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/dashboard")} className="gap-2 cursor-pointer">
          <LayoutGrid size={14} />
          {t("nav.myCards", { defaultValue: "My Cards" })}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/wishlists")} className="gap-2 cursor-pointer">
          <Gift size={14} />
          {t("nav.wishlists", { defaultValue: "Wish Lists" })}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/profile?tab=preferences")} className="gap-2 cursor-pointer">
          <Settings size={14} />
          {t("nav.settings", { defaultValue: "Settings" })}
        </DropdownMenuItem>

        {isAdmin && (
          <DropdownMenuItem onClick={() => navigate("/admin")} className="gap-2 cursor-pointer text-primary">
            <Shield size={14} />
            {t("nav.adminDashboard", { defaultValue: "Admin Dashboard" })}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut} className="gap-2 cursor-pointer text-muted-foreground">
          <LogOut size={14} />
          {t("nav.logout", { defaultValue: "Logout" })}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
